import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { alertsAPI, certificationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  TrendingUp,
  Calendar,
  Mail,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Shield,
  AlertTriangle,
  PartyPopper,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const OnboardingDashboard = () => {
  const { canManage } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [expiringCerts, setExpiringCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('progress');

  useEffect(() => {
    if (canManage) {
      loadAllData();
    }
  }, [canManage]);

  const loadAllData = async () => {
    try {
      const [dashboardRes, alertsRes, certsRes] = await Promise.all([
        api.get('/onboarding-progress-dashboard'),
        alertsAPI.getAlerts().catch(() => ({ data: [] })),
        certificationsAPI.getExpiring(90).catch(() => ({ data: [] }))
      ]);
      setDashboardData(dashboardRes.data);
      setAlerts(alertsRes.data || []);
      setExpiringCerts(certsRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />In Progress</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600"><AlertCircle className="w-3 h-3 mr-1" />Not Started</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredEmployees = dashboardData?.employees?.filter(emp => {
    const matchesSearch = emp.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         emp.user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || emp.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  // Count alerts by type
  const alertCounts = {
    critical: alerts.filter(a => a.severity === 'critical').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    success: alerts.filter(a => a.severity === 'success').length,
  };

  if (!canManage) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Admin or Lead access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  const summary = dashboardData?.summary || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onboarding-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            Team Progress Dashboard
          </h1>
          <p className="text-slate-600 mt-1">
            Track onboarding completion and compliance certifications
          </p>
        </div>
        {(alertCounts.critical > 0 || alertCounts.warning > 0) && (
          <div className="flex items-center gap-2">
            {alertCounts.critical > 0 && (
              <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {alertCounts.critical} Critical
              </Badge>
            )}
            {alertCounts.warning > 0 && (
              <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
                <Bell className="w-4 h-4 mr-1" />
                {alertCounts.warning} Warnings
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Alerts Section - Always visible if there are alerts */}
      {alerts.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-white">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Action Required
              <Badge variant="outline" className="ml-2">{alerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {alerts.slice(0, 5).map((alert, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                    alert.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                    'bg-emerald-50 border-emerald-200'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    alert.severity === 'critical' ? 'bg-red-100' :
                    alert.severity === 'warning' ? 'bg-amber-100' :
                    'bg-emerald-100'
                  }`}>
                    {alert.type === 'cert_expiring' && <Shield className={`w-4 h-4 ${alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />}
                    {alert.type === 'low_progress' && <AlertCircle className="w-4 h-4 text-amber-600" />}
                    {alert.type === 'onboarding_complete' && <PartyPopper className="w-4 h-4 text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${
                      alert.severity === 'critical' ? 'text-red-800' :
                      alert.severity === 'warning' ? 'text-amber-800' :
                      'text-emerald-800'
                    }`}>
                      {alert.message}
                    </p>
                    <p className="text-xs text-slate-500">{alert.user_email}</p>
                  </div>
                  {alert.type !== 'onboarding_complete' && (
                    <a 
                      href={`mailto:${alert.user_email}?subject=${
                        alert.type === 'cert_expiring' 
                          ? `Certification Renewal Reminder - ${alert.cert_type}`
                          : 'Onboarding Progress Check-in'
                      }`}
                      className="flex-shrink-0"
                    >
                      <Button variant="outline" size="sm">
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-slate-200 shadow-card">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{summary.total_employees || 0}</p>
            <p className="text-sm text-slate-500">Total Employees</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 shadow-card bg-emerald-50">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
            <p className="text-2xl font-bold text-emerald-700">{summary.completed || 0}</p>
            <p className="text-sm text-emerald-600">Completed</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 shadow-card bg-amber-50">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-amber-700">{summary.in_progress || 0}</p>
            <p className="text-sm text-amber-600">In Progress</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 shadow-card bg-red-50">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
            <p className="text-2xl font-bold text-red-700">{summary.not_started || 0}</p>
            <p className="text-sm text-red-600">Not Started</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 shadow-card bg-blue-50">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-blue-700">{summary.average_progress || 0}%</p>
            <p className="text-sm text-blue-600">Avg Progress</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Progress and Certifications */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="progress" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Onboarding Progress
          </TabsTrigger>
          <TabsTrigger value="certifications" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Expiring Certifications
            {expiringCerts.length > 0 && (
              <Badge variant="outline" className="ml-1 text-xs">{expiringCerts.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="progress" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="not_started">Not Started</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadAllData}>
              Refresh
            </Button>
          </div>

          {/* Employee Progress List */}
          <Card className="border-slate-200 shadow-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-lg font-heading">Employee Onboarding Progress</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {filteredEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No employees found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredEmployees.map((employee) => (
                    <div key={employee.user.id} className="hover:bg-slate-50 transition-colors">
                      {/* Main Row */}
                      <div 
                        className="flex items-center gap-4 p-4 cursor-pointer"
                        onClick={() => setExpandedUser(expandedUser === employee.user.id ? null : employee.user.id)}
                        data-testid={`employee-row-${employee.user.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold flex-shrink-0">
                          {employee.user.name.charAt(0).toUpperCase()}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{employee.user.name}</p>
                            {getStatusBadge(employee.status)}
                            {employee.progress_percent < 30 && employee.status !== 'completed' && (
                              <Badge className="bg-red-100 text-red-700 text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Behind
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {employee.user.email}
                            </span>
                            {employee.user.job_role && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3 h-3" />
                                {employee.user.job_role}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="w-48 flex-shrink-0">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-slate-600">{employee.completed_items}/{employee.total_items}</span>
                            <span className={`font-semibold ${
                              employee.progress_percent >= 75 ? 'text-emerald-600' :
                              employee.progress_percent >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>{employee.progress_percent}%</span>
                          </div>
                          <Progress value={employee.progress_percent} className="h-2" />
                        </div>
                        
                        <div className="text-right flex-shrink-0 w-28">
                          <p className="text-xs text-slate-500">Last Activity</p>
                          <p className="text-sm text-slate-700">{formatDate(employee.last_activity)}</p>
                        </div>
                        
                        <a 
                          href={`mailto:${employee.user.email}?subject=Onboarding Progress Check-in`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-shrink-0"
                        >
                          <Button variant="outline" size="sm">
                            <Mail className="w-4 h-4" />
                          </Button>
                        </a>
                        
                        <div className="flex-shrink-0">
                          {expandedUser === employee.user.id ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Section Progress */}
                      {expandedUser === employee.user.id && (
                        <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
                            {Object.entries(employee.section_progress || {}).map(([section, data]) => (
                              <div key={section} className="bg-white p-3 rounded-lg border border-slate-200">
                                <p className="text-sm font-medium text-slate-700 mb-2">{section}</p>
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-slate-500">{data.completed}/{data.total}</span>
                                  <span className={`font-semibold ${
                                    data.completed === data.total ? 'text-emerald-600' : 'text-slate-600'
                                  }`}>
                                    {Math.round((data.completed / data.total) * 100)}%
                                  </span>
                                </div>
                                <Progress 
                                  value={(data.completed / data.total) * 100} 
                                  className="h-1.5" 
                                />
                                {data.completed === data.total && (
                                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" /> Complete
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 text-xs text-slate-500">
                            Assigned: {formatDate(employee.assigned_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certifications" className="space-y-4">
          <Card className="border-slate-200 shadow-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Certifications Expiring (Next 90 Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {expiringCerts.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="w-16 h-16 mx-auto text-emerald-300 mb-4" />
                  <p className="text-emerald-600 font-medium">All Clear!</p>
                  <p className="text-slate-500">No certifications expiring in the next 90 days</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {expiringCerts.map((cert) => (
                    <div 
                      key={cert.id}
                      className={`flex items-center gap-4 p-4 ${
                        cert.urgency === 'critical' ? 'bg-red-50' :
                        cert.urgency === 'warning' ? 'bg-amber-50' : ''
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        cert.urgency === 'critical' ? 'bg-red-100' :
                        cert.urgency === 'warning' ? 'bg-amber-100' :
                        'bg-slate-100'
                      }`}>
                        <Shield className={`w-5 h-5 ${
                          cert.urgency === 'critical' ? 'text-red-600' :
                          cert.urgency === 'warning' ? 'text-amber-600' :
                          'text-slate-600'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{cert.user_name}</p>
                          <Badge className={
                            cert.cert_type === 'GCP' ? 'bg-blue-100 text-blue-700' :
                            cert.cert_type === 'HIPAA' ? 'bg-purple-100 text-purple-700' :
                            'bg-amber-100 text-amber-700'
                          }>
                            {cert.cert_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{cert.cert_name}</p>
                        <p className="text-xs text-slate-400">{cert.user_email}</p>
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <Badge className={
                          cert.urgency === 'critical' ? 'bg-red-100 text-red-700' :
                          cert.urgency === 'warning' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          <Clock className="w-3 h-3 mr-1" />
                          {cert.days_until_expiry} days left
                        </Badge>
                        <p className="text-xs text-slate-500 mt-1">
                          Expires: {formatDate(cert.expiry_date)}
                        </p>
                      </div>
                      
                      <a 
                        href={`mailto:${cert.user_email}?subject=Certification Renewal Reminder - ${cert.cert_type}&body=Hi ${cert.user_name},%0D%0A%0D%0AThis is a reminder that your ${cert.cert_name} certification expires on ${formatDate(cert.expiry_date)}.%0D%0A%0D%0APlease ensure you complete the renewal process before the expiry date.%0D%0A%0D%0AThank you.`}
                        className="flex-shrink-0"
                      >
                        <Button variant="outline" size="sm">
                          <Mail className="w-4 h-4 mr-1" />
                          Remind
                        </Button>
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OnboardingDashboard;
